import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext, Controller } from "react-hook-form";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { Box, MenuItem, Select, TextField, Typography } from "@mui/material";

import { currencyList } from "../../../../../enums/currencies.enum";
import { usePaymentAccounts } from "../../../../../api/queries/payment/paymentQueries";

const formFieldStyle = {
  display: "flex",
  flexDirection: "column",
  rowGap: "6px",
};

const PaymentDetails = () => {
  const { t } = useTranslation();
  const { userPaymentAccount, isUserPaymentAccountLoading, isUserPaymentAccountHasError } = usePaymentAccounts();
  const isWalletAccount = userPaymentAccount?.type === "e_wallet";

  const {
    control,
    formState: { errors },
  } = useFormContext();
  const categories = [
    {
      label: t("pages.payment.createPaymentLink.service"),
      value: "SERVICE",
    },
    {
      label: t("pages.payment.createPaymentLink.membership"),
      value: "MEMBERSHIP",
    },
    {
      label: t("pages.payment.createPaymentLink.product"),
      value: "PRODUCT",
    },
  ];

  const [openMuiDatePicker, setOpenMuiDatePicker] = useState(false);
  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", rowGap: "24px" }}>
      <Box sx={formFieldStyle}>
        <Typography
          component="label"
          sx={{
            color: "#111",
            fontFamily: "Montserrat",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "24px",
          }}
        >
          {t("pages.payment.createPaymentLink.amount")}
        </Typography>
        <Box>
          <Box
            sx={{
              display: "flex",
              border: "1px solid",
              borderRadius: "33px",
              width: "100%",
              borderColor: errors.amount ? "red" : "#CFCFCF",
            }}
          >
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  error={!!errors.amount}
                  placeholder="1000"
                  inputProps={{ maxLength: 6 }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none !important",
                    },
                    "& .MuiInputBase-root": {
                      border: "none !important",
                    },
                  }}
                />
              )}
            />
            <Controller
              name="currency"
              control={control}
              defaultValue={"EGP"}
              render={({ field, fieldState }) => (
                <Select
                  {...field}
                  error={!!fieldState.error}
                  disabled={isUserPaymentAccountLoading}
                  sx={{
                    borderRadius: "unset !important",
                    borderTop: "none !important",
                    borderBottom: "none !important",
                    borderRight: "none !important",
                    borderLeft: "1px solid",
                    borderLeftColor: errors.amount ? "red" : "#CFCFCF",
                    paddingLeft: "0",
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none !important",
                    },
                    "& .MuiInputBase-root.MuiOutlinedInput-root": {
                      border: "none !important",
                    },
                    "& .MuiSelect-select.MuiSelect-outlined.MuiInputBase-input": {
                      padding: "8px 16px 8px 8px",
                    },
                  }}
                >
                  {currencyList.map((currency) => {
                    if (isWalletAccount && currency !== "EGP") return null;
                    return (
                      <MenuItem key={currency} value={currency}>
                        {currency}
                      </MenuItem>
                    );
                  })}
                </Select>
              )}
            />
          </Box>
          {errors.amount?.message && (
            <Typography
              sx={{
                color: "red",
                fontSize: "12px",
                fontWeight: 500,
                marginTop: "4px",
              }}
            >
              {t(`${String(errors.amount.message)}`)}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={formFieldStyle}>
        <Typography
          component="label"
          sx={{
            color: "#111",
            fontFamily: "Montserrat",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "24px",
          }}
        >
          {t("pages.payment.createPaymentLink.paymentFor")}
        </Typography>
        <Controller
          name="paymentFor"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              multiline
              rows={6}
              onBlur={field.onBlur}
              error={!!errors.paymentFor}
              helperText={
                errors.paymentFor && t(`pages.payment.createPaymentLink.${String(errors.paymentFor.message)}`)
              }
              placeholder={t("pages.payment.createPaymentLink.paymentForPlaceHolder")}
              sx={{
                "& .MuiInputBase-root.MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
                "& .MuiFormHelperText-root.Mui-error": {
                  color: "red",
                },
              }}
            />
          )}
        />
      </Box>
      <Box sx={formFieldStyle}>
        <Typography
          component="label"
          sx={{
            color: "#111",
            fontFamily: "Montserrat",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "24px",
          }}
        >
          {t("pages.payment.createPaymentLink.paymentCategory")}
        </Typography>
        <Controller
          name="paymentCategory"
          control={control}
          render={({ field, fieldState }) => (
            <Select sx={{ "& .MuiSelect-select": { color: "#000000de" } }} {...field} error={!!fieldState.error}>
              {categories.map((category) => (
                <MenuItem key={category.value} value={category.value}>
                  {category.label}
                </MenuItem>
              ))}
            </Select>
          )}
        />
        {errors.paymentCategory?.message && (
          <Typography
            sx={{
              color: "red",
              fontSize: "12px",
              fontWeight: 500,
              marginTop: "4px",
            }}
          >
            {t(`pages.payment.createPaymentLink.${String(errors.paymentCategory.message)}`)}
          </Typography>
        )}
      </Box>
      <Box sx={formFieldStyle}>
        <Typography
          component="label"
          sx={{
            color: "#111",
            fontFamily: "Montserrat",
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "24px",
          }}
        >
          {t("pages.payment.createPaymentLink.expiryDate")}
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Controller
            name="expiryDate"
            control={control}
            render={({ field }) => (
              <DatePicker
                {...field}
                slots={{
                  textField: TextField,
                }}
                slotProps={{
                  textField: {
                    onClick: () => setOpenMuiDatePicker(true),
                    onFocus: (e) => e.preventDefault(),
                  },
                }}
                open={openMuiDatePicker}
                onOpen={() => setOpenMuiDatePicker(true)}
                onClose={() => setOpenMuiDatePicker(false)}
                onChange={(date) => {
                  if (date) {
                    date.setHours(23, 59, 59, 999);
                  }
                  field.onChange(date);
                }}
                minDate={new Date()}
                sx={{
                  "& .MuiInputBase-root.MuiOutlinedInput-root": {
                    borderColor: errors.expiryDate ? "red !important" : "#CFCFCF",
                  },
                }}
              />
            )}
          />
        </LocalizationProvider>
        {errors.expiryDate?.message && (
          <Typography
            sx={{
              color: "red",
              fontSize: "12px",
              fontWeight: 500,
              marginTop: "4px",
            }}
          >
            {t(`pages.payment.createPaymentLink.${String(errors.expiryDate.message)}`)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PaymentDetails;
